/* eslint-disable no-bitwise */
import baseStyled, { ThemeProvider, useTheme as useThemeBase, css as baseCss, withTheme as baseWithTheme } from 'styled-components';
import { useMemo, useState } from 'react';
import { useTheme as useFoundationsTheme } from 'foundations-theming/utils';
import { calendarDefaultProps, useCalendarProps } from '../internal/CalendarPropsProvider';
import { isAfter, isBefore, isSame } from './time';
import { jsx as _jsx } from "react/jsx-runtime";
export const remCalc = size => {
  return `${size}px`;
};
export const getSize = value => typeof value === 'number' ? `${value}px` : value;
export const hexToRgb = hex => {
  const bigint = parseInt(hex, 16);
  const r = bigint >> 16 & 255;
  const g = bigint >> 8 & 255;
  const b = bigint & 255;
  return [r, g, b].join();
};
export const isCssUnit = value => value ? value.match(/^\d+(\.\d+)?(vh|vw|px|em|rem|%)$/) !== null : false;

/**
 * We store values on theme mostly as CssUnits so downstream components can use them directly.
 * In cases we want to manipulate units we can use CSS calc. In scenarios where we need to
 * manipulate the value in JavaScript we can use this function to get the raw number.
 */
const raw = value => {
  return parseInt(value.replace(/^[^0-9]*$/, ''), 10);
};
export const makeCalendarTheme = ({
  hourHeight,
  hideWeekends,
  themeOverride = {},
  use,
  timezone,
  displayYear,
  displayMonth,
  currentTime
} = calendarDefaultProps) => {
  var _themeOverride$monthV, _themeOverride$monthV2, _themeOverride$dayVie, _themeOverride$dayVie2, _themeOverride$header, _themeOverride$dayCur, _themeOverride$header2, _themeOverride$day, _themeOverride$day2, _themeOverride$day3, _themeOverride$hour, _themeOverride$hour2, _themeOverride$hour3;
  const weekDayNumber = hideWeekends ? 5 : 7;
  const monthViewDayHeight = (_themeOverride$monthV = (_themeOverride$monthV2 = themeOverride.monthView) === null || _themeOverride$monthV2 === void 0 ? void 0 : _themeOverride$monthV2.dayHeight) !== null && _themeOverride$monthV !== void 0 ? _themeOverride$monthV : '13.4vh';
  const dayViewHourHeight = (_themeOverride$dayVie = (_themeOverride$dayVie2 = themeOverride.dayView) === null || _themeOverride$dayVie2 === void 0 ? void 0 : _themeOverride$dayVie2.hourHeight) !== null && _themeOverride$dayVie !== void 0 ? _themeOverride$dayVie : remCalc(hourHeight !== null && hourHeight !== void 0 ? hourHeight : 96);
  const headerHeight = (_themeOverride$header = themeOverride.headerHeight) !== null && _themeOverride$header !== void 0 ? _themeOverride$header : remCalc(60);
  const dayCurrentDateMarkerBackground = (_themeOverride$dayCur = themeOverride.dayCurrentDateMarkerBackground) !== null && _themeOverride$dayCur !== void 0 ? _themeOverride$dayCur : 'fill-alert-default';
  const theme = {
    dayWidthPercent: use === 'DAY' ? 100 : 100 / weekDayNumber,
    weekDayNumber,
    headerHeight,
    headerBackground: (_themeOverride$header2 = themeOverride.headerBackground) !== null && _themeOverride$header2 !== void 0 ? _themeOverride$header2 : 'fill-transitional-table-default',
    eventMargin: remCalc(1),
    dateNumberHeight: remCalc(20),
    dayCurrentDateMarkerBackground,
    monthView: {
      dayHeight: monthViewDayHeight,
      eventHeight: remCalc(24),
      eventTopPadding: remCalc(35)
    },
    weekView: {
      timeRowPadding: remCalc(16),
      spacerWidth: remCalc(96),
      spacerWidthWithoutToggle: remCalc(14)
    },
    dayView: {
      hourHeight: dayViewHourHeight
    },
    event: Object.assign({
      blockSize: '100%',
      alignItems: 'start'
    }, use === 'MONTH' && {
      alignItems: 'center',
      // TODO: this has to be reconciled with `monthView.eventHeight` including enabling all the dynamic height logic
      blockSize: '24px'
    }),
    calendarViewShell: Object.assign({}, use !== 'MONTH' && {
      maxBlockSize: '73vh'
    }, themeOverride.calendarViewShell, {
      flexDirection: 'column',
      position: 'relative'
    }),
    allDayEvents: {
      numberOfColumns: use === 'WEEK' ? weekDayNumber : 1,
      maxHeight: remCalc(156),
      minHeight: remCalc(96)
    },
    day: {
      past: Object.assign({}, (_themeOverride$day = themeOverride.day) === null || _themeOverride$day === void 0 ? void 0 : _themeOverride$day.past),
      current: Object.assign({
        backgroundColor: 'fill-surface-recessed'
      }, (_themeOverride$day2 = themeOverride.day) === null || _themeOverride$day2 === void 0 ? void 0 : _themeOverride$day2.current),
      future: Object.assign({}, (_themeOverride$day3 = themeOverride.day) === null || _themeOverride$day3 === void 0 ? void 0 : _themeOverride$day3.future)
    },
    hour: {
      past: Object.assign({}, (_themeOverride$hour = themeOverride.hour) === null || _themeOverride$hour === void 0 ? void 0 : _themeOverride$hour.past),
      current: Object.assign({}, (_themeOverride$hour2 = themeOverride.hour) === null || _themeOverride$hour2 === void 0 ? void 0 : _themeOverride$hour2.current),
      future: Object.assign({}, (_themeOverride$hour3 = themeOverride.hour) === null || _themeOverride$hour3 === void 0 ? void 0 : _themeOverride$hour3.future)
    }
  };
  return Object.assign({}, theme, {
    util: {
      raw,
      eventWidth({
        totalEvents,
        lastChild = false
      }) {
        return `calc(${100 / totalEvents}% - ${theme.eventMargin} * ${lastChild ? 2 : 1})`;
      },
      maxMonthEventDisplayCount(calendarHeight, numberOfMonthRows) {
        const {
          dateNumberHeight,
          monthView: {
            eventHeight,
            eventTopPadding
          }
        } = theme;
        const overflowAllowance = 5;
        const monthRowHeightPercentage = 100 / numberOfMonthRows;
        const currentHeight = calendarHeight * (monthRowHeightPercentage / 100) - raw(eventTopPadding);
        return Math.floor((currentHeight - raw(dateNumberHeight) + overflowAllowance) / raw(eventHeight));
      },
      getCurrentTimeTopPosition({
        hours,
        minutes
      }) {
        const rawHourHeight = raw(dayViewHourHeight);
        const heightOnTheHour = rawHourHeight * minutes / 60;
        const heightOnCalendar = rawHourHeight * hours;
        return heightOnTheHour + heightOnCalendar + rawHourHeight / 2;
      },
      getTemporalModifiers({
        forMoment,
        magnitude
      }) {
        if (displayYear === undefined || displayMonth === undefined || forMoment === undefined) {
          return {};
        }
        const referenceDate = forMoment.clone().minute(0).second(0).millisecond(0);
        const lookup = magnitude === 'day' ? theme.day : theme.hour;
        if (isBefore(referenceDate, currentTime, timezone, magnitude)) {
          return lookup.past;
        } else if (isAfter(referenceDate, currentTime, timezone, magnitude)) {
          return lookup.future;
        } else if (isSame(currentTime, referenceDate, timezone, magnitude)) {
          return lookup.current;
        }
      }
    }
  });
};
export const ThemeEnhancer = ({
  children
}) => {
  const theme = useFoundationsTheme();
  const props = useCalendarProps();
  const newTheme = useMemo(() => {
    return Object.assign({}, theme, {
      uiAddonCalendars: makeCalendarTheme(props)
    });
  }, [theme, props]);
  return /*#__PURE__*/_jsx(ThemeProvider, {
    theme: newTheme,
    children: children
  });
};
export * from 'styled-components';
export const styled = baseStyled;
export const css = baseCss;
export const withTheme = baseWithTheme;
export const useTheme = () => useThemeBase();
export const useCalendarThemeOverride = override => {
  const [theme, updateTheme] = useState(override);
  return [theme, updateTheme];
};